import { ConnectMailboxButtons } from "@/connect_mailbox/connect_mailbox_buttons"
import { Button } from "@/components/ui/button.tsx"
import { useAsyncEffect } from "@/utils"
import { DateTime } from "luxon"
import { useState } from "preact/compat"
import { useLocation } from "wouter-preact"
import { use_connect_redirect_to } from "../use_connect_redirect_to"
import { CenterColumnContainer } from "@/components/LoginScreen.tsx"
import {
  findIntegrationByName,
  getIntegrations,
  IntegrationWithJobInfo,
  upsertIntegration,
  updateIntegration,
} from "@/api/integration"

const WINCO_NAME = "winco"
// There is no login, so there is no meaningful username — a stable placeholder keeps the upsert
// idempotent, the same trick The Vitamin Shoppe, Thrive and Southern Glazer's use. Must match
// `STABLE_USERNAME` in server/src/integration/winco/ingest.rs.
const STABLE_USERNAME = "default"

export type WincoConnectionState = "not_connected" | "connected" | "paused"

/**
 * What to tell the brand about the connection. The server enrols an org the first time a genuine
 * advice arrives, so "connected" usually happened without anyone clicking — the page says so
 * rather than asking them to set up something that already runs.
 */
export function winco_connection_state(integration: IntegrationWithJobInfo | undefined): WincoConnectionState {
  if (!integration) return "not_connected"
  return integration.enabled ? "connected" : "paused"
}

export function winco_status_caption(state: WincoConnectionState, last_sync_at: string | null | undefined): string {
  switch (state) {
    case "connected": {
      if (!last_sync_at) return "Connected — waiting for the first EFT payment notification to sync."
      const when = DateTime.fromISO(last_sync_at).toLocaleString(DateTime.DATETIME_MED)
      return `Connected — last synced ${when}.`
    }
    case "paused":
      return "Paused — EFT payment notifications in the mailbox are not being booked."
    case "not_connected":
      return "Not connected yet. It switches on by itself the first time a WinCo EFT payment notification reaches a connected mailbox, or you can turn it on now."
  }
}

function MailboxList({ mailboxes }: { mailboxes: IntegrationWithJobInfo[] }) {
  return (
    <div class="border border-gray-200 rounded-lg p-4 space-y-2">
      <h3 class="font-medium text-gray-900 text-sm">Connected mailboxes</h3>
      {mailboxes.map((mailbox: IntegrationWithJobInfo) => {
        const updated = mailbox.session_updated_at
          ? DateTime.fromISO(mailbox.session_updated_at).toLocaleString(DateTime.DATETIME_MED)
          : ""
        return (
          <div key={mailbox.id} class="flex justify-between gap-4 text-sm">
            <span class="text-gray-700 truncate">
              {mailbox.name}: {mailbox.username}
            </span>
            {updated ? <span class="text-gray-400 shrink-0">{updated}</span> : null}
          </div>
        )
      })}
    </div>
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function ConnectWincoPage(props: any = {}) {
  const redirectTo = use_connect_redirect_to(props.redirectTo)
  const [, setLocation] = useLocation()

  const [emailIntegrations, setEmailIntegrations] = useState<IntegrationWithJobInfo[]>([])
  const [integration, setIntegration] = useState<IntegrationWithJobInfo | undefined>(undefined)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function reload() {
    const res = await getIntegrations()
    if (!res.ok) {
      setError("Could not load integrations")
      return
    }
    const all = res.value.data
    setEmailIntegrations(all.filter((i: IntegrationWithJobInfo) => i.name === "gmail" || i.name === "microsoft"))
    setIntegration(findIntegrationByName(all, WINCO_NAME))
  }

  useAsyncEffect(reload, [])

  const state = winco_connection_state(integration)
  const has_mailbox = emailIntegrations.length > 0

  async function handleTurnOn() {
    setLoading(true)
    setError(null)
    if (integration) {
      const res = await updateIntegration(integration.id, { enabled: true })
      if (!res.ok) setError("Could not resume the connection")
    } else {
      const res = await upsertIntegration(WINCO_NAME, STABLE_USERNAME, {})
      if (!res.ok) setError("Could not enable the connection")
    }
    await reload()
    setLoading(false)
  }

  async function handlePause() {
    if (!integration) return
    setLoading(true)
    setError(null)
    const res = await updateIntegration(integration.id, { enabled: false })
    if (!res.ok) setError("Could not pause the connection")
    await reload()
    setLoading(false)
  }

  return (
    <CenterColumnContainer>
      <div class="space-y-6 max-w-md mx-auto">
        <div class="flex items-center justify-center">
          <span class="text-3xl font-black tracking-tight text-gray-900">WinCo Foods</span>
        </div>
        <h1 class="text-xl font-semibold text-gray-900 text-center">Sync WinCo Foods from email</h1>
        <p class="text-gray-500 text-sm text-center">
          WinCo has no vendor deduction portal. Each EFT comes as an "EFT PAYMENT NOTIFICATION" email from
          WINCOREPORTS@WINCOFOODS.COM with a one-page payment breakdown PDF listing the invoices paid and the
          chargebacks, reclamation and audit claims netted out. We read those from the mailboxes connected below
          and book each line as a transaction, the moment they arrive and again on a daily weekday pass. The
          reclamation reports and buyer chargeback documents WinCo emails are attached to the lines they explain.
        </p>
        <p class="text-gray-500 text-sm text-center">
          WinCo only emails chargeback documents and reclamation reports to vendors on its auto-email list — ask
          backup@wincofoods.com to add the connected mailbox.
        </p>

        <div
          class={`text-sm rounded-lg p-3 border ${
            state === "connected"
              ? "text-emerald-800 bg-emerald-50 border-emerald-200"
              : state === "paused"
                ? "text-amber-800 bg-amber-50 border-amber-200"
                : "text-gray-700 bg-gray-50 border-gray-200"
          }`}>
          {winco_status_caption(state, integration?.last_sync_job?.created_at)}
        </div>

        <div class="space-y-3">
          <p class="text-sm font-medium text-gray-800">Connect email</p>
          <ConnectMailboxButtons gmailLabel="Add Gmail" microsoftLabel="Add Microsoft 365" onChange={reload} />
        </div>

        {has_mailbox ? (
          <MailboxList mailboxes={emailIntegrations} />
        ) : (
          <p class="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
            No mailbox is connected yet. Connect the inbox that receives (or is forwarded) WinCo's EFT payment
            notifications — nothing can be read until then.
          </p>
        )}

        {error ? <p class="text-sm text-red-600">{error}</p> : null}

        <div class="flex justify-between gap-3">
          <Button type="button" variant="outline" onClick={() => setLocation(redirectTo)}>
            Back
          </Button>
          {state === "connected" ? (
            <Button type="button" variant="outline" onClick={() => void handlePause()} disabled={loading}>
              {loading ? "Pausing…" : "Pause"}
            </Button>
          ) : (
            <Button type="button" onClick={() => void handleTurnOn()} disabled={loading} class="bg-gray-900 text-white">
              {loading ? "Turning on…" : state === "paused" ? "Resume" : "Turn on now"}
            </Button>
          )}
        </div>
      </div>
    </CenterColumnContainer>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  function integration(partial: Partial<IntegrationWithJobInfo>): IntegrationWithJobInfo {
    return { id: "id", name: "winco", username: "default", enabled: true, ...partial } as IntegrationWithJobInfo
  }

  describe("winco_connection_state", () => {
    it("is not connected without a row, connected when enabled, paused when disabled", () => {
      expect(winco_connection_state(undefined)).toBe("not_connected")
      expect(winco_connection_state(integration({ enabled: true }))).toBe("connected")
      expect(winco_connection_state(integration({ enabled: false }))).toBe("paused")
    })
  })

  describe("winco_status_caption", () => {
    it("tells a connected brand when it last synced, or that it is waiting", () => {
      expect(winco_status_caption("connected", null)).toContain("waiting for the first")
      expect(winco_status_caption("connected", "2026-08-19T12:00:00Z")).toMatch(/^Connected — last synced /)
    })
    it("explains that an unconnected brand does not have to do anything", () => {
      expect(winco_status_caption("not_connected", null)).toContain("switches on by itself")
      expect(winco_status_caption("paused", null)).toContain("Paused")
    })
  })
}
